import { cn } from '@/lib/utils';
import type {
  DeadlineType,
  EntryPath,
  GoNoGo,
  ProgramLayerId,
} from '@/types/roadmap.types';
import { getLayerShortLabel } from '@/lib/investment-layers';

// ---------------------------------------------------------------------------
// Unified chip system
//
// Every chip in the roadmap app renders through one of the helpers below.
// There are three visual shapes, applied consistently wherever a given chip
// type appears (table, cards, headers, timelines, print, etc.):
//
//   • tone="minimal"  – colored dot + uppercase label, transparent bg.
//                       Used for Status and Difficulty (scannable meta).
//   • tone="soft"     – pill with a subtle tinted bg + uppercase label.
//                       Used for Objective and Entry Path (taxonomy).
//   • tone="solid"    – filled pill with white uppercase label.
//                       Used for Action (the primary/secondary/tertiary tier).
// ---------------------------------------------------------------------------

type ChipTone = 'minimal' | 'soft' | 'solid';

interface ChipProps {
  tone: ChipTone;
  label: string;
  /** Tailwind class string controlling color. Shape is set by `tone`. */
  colorClass: string;
  /** Optional dot-color override for `tone="minimal"`. Defaults to colorClass. */
  dotClass?: string;
  className?: string;
}

const SHAPE: Record<ChipTone, string> = {
  minimal:
    'inline-flex items-center gap-1.5 text-[11px] print:text-[7pt] font-semibold tracking-wide uppercase whitespace-nowrap',
  soft:
    'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] print:text-[7pt] font-semibold tracking-wide uppercase whitespace-nowrap',
  solid:
    'inline-flex items-center rounded-full px-3 py-1 text-[11px] print:text-[7pt] font-semibold tracking-wide uppercase whitespace-nowrap shadow-[0_1px_0_rgb(0_0_0_/_0.04)]',
};

export function Chip({ tone, label, colorClass, dotClass, className }: ChipProps) {
  if (tone === 'minimal') {
    return (
      <span className={cn(SHAPE.minimal, 'text-muted-foreground', className)}>
        <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', dotClass ?? colorClass)} />
        <span>{label}</span>
      </span>
    );
  }
  return <span className={cn(SHAPE[tone], colorClass, className)}>{label}</span>;
}

// ---------------------------------------------------------------------------
// Status (deadline type)
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<DeadlineType, string> = {
  specific: 'Firm',
  rolling: 'Rolling',
  monthly: 'Rolling',
  expected: 'Expected',
};

const STATUS_DOT: Record<DeadlineType, string> = {
  specific: 'bg-rose-500',
  rolling: 'bg-sky-500',
  monthly: 'bg-sky-500',
  expected: 'bg-amber-500',
};

export function StatusChip({ value, className }: { value: DeadlineType; className?: string }) {
  return (
    <Chip
      tone="minimal"
      label={STATUS_LABELS[value]}
      colorClass={STATUS_DOT[value]}
      className={className}
    />
  );
}

// ---------------------------------------------------------------------------
// Difficulty (derived from application_burden score)
// ---------------------------------------------------------------------------

type DifficultyTier = 'low' | 'moderate' | 'high' | 'very_high';

const DIFFICULTY_LABELS: Record<DifficultyTier, string> = {
  low: 'Low',
  moderate: 'Moderate',
  high: 'High',
  very_high: 'Very High',
};

const DIFFICULTY_DOT: Record<DifficultyTier, string> = {
  low: 'bg-emerald-500',
  moderate: 'bg-amber-500',
  high: 'bg-orange-500',
  very_high: 'bg-rose-500',
};

export function getDifficultyTier(burdenScore: number): DifficultyTier {
  if (burdenScore >= 5) return 'low';
  if (burdenScore >= 4) return 'moderate';
  if (burdenScore >= 3) return 'high';
  return 'very_high';
}

export function DifficultyChip({
  burdenScore,
  className,
}: {
  burdenScore: number;
  className?: string;
}) {
  const tier = getDifficultyTier(burdenScore);
  return (
    <Chip
      tone="minimal"
      label={DIFFICULTY_LABELS[tier]}
      colorClass={DIFFICULTY_DOT[tier]}
      className={className}
    />
  );
}

// ---------------------------------------------------------------------------
// Objective (the investment layer – formerly "Best For")
// ---------------------------------------------------------------------------

const OBJECTIVE_COLORS: Record<string, string> = {
  rd: 'bg-violet-50 text-violet-700 border-violet-200 print:bg-violet-50',
  testing: 'bg-sky-50 text-sky-700 border-sky-200 print:bg-sky-50',
  prototyping: 'bg-amber-50 text-amber-700 border-amber-200 print:bg-amber-50',
  manufacturing: 'bg-emerald-50 text-emerald-700 border-emerald-200 print:bg-emerald-50',
  general: 'bg-slate-50 text-slate-600 border-slate-200 print:bg-slate-50',
  commercial: 'bg-rose-50 text-rose-700 border-rose-200 print:bg-rose-50',
  deployment: 'bg-teal-50 text-teal-700 border-teal-200 print:bg-teal-50',
  scaling: 'bg-orange-50 text-orange-700 border-orange-200 print:bg-orange-50',
};

export function ObjectiveChip({
  value,
  className,
}: {
  value: ProgramLayerId;
  className?: string;
}) {
  const color = OBJECTIVE_COLORS[value] ?? OBJECTIVE_COLORS.general;
  return (
    <Chip
      tone="soft"
      label={getLayerShortLabel(value)}
      colorClass={color}
      className={className}
    />
  );
}

// ---------------------------------------------------------------------------
// Entry Path (how the program is accessed / who applies)
// ---------------------------------------------------------------------------

const ENTRY_PATH_LABELS: Record<EntryPath, string> = {
  direct: 'Direct',
  reimbursement: 'Reimbursement',
  partner_led: 'Partner-Led',
  vendor: 'Vendor',
  consortium: 'Consortium',
};

const ENTRY_PATH_COLORS: Record<EntryPath, string> = {
  direct: 'bg-emerald-50 text-emerald-700 border-emerald-200 print:bg-emerald-50',
  reimbursement: 'bg-amber-50 text-amber-700 border-amber-200 print:bg-amber-50',
  partner_led: 'bg-violet-50 text-violet-700 border-violet-200 print:bg-violet-50',
  vendor: 'bg-sky-50 text-sky-700 border-sky-200 print:bg-sky-50',
  consortium: 'bg-indigo-50 text-indigo-700 border-indigo-200 print:bg-indigo-50',
};

export function EntryPathChip({
  value,
  className,
}: {
  value: EntryPath | null | undefined;
  className?: string;
}) {
  if (!value) return null;
  return (
    <Chip
      tone="soft"
      label={ENTRY_PATH_LABELS[value]}
      colorClass={ENTRY_PATH_COLORS[value]}
      className={className}
    />
  );
}

// ---------------------------------------------------------------------------
// Action (the call-to-action chip)
//
// Four solid-filled tiers, rendered in priority order:
//   primary      – navy solid   "Primary"
//   go           – green solid  "Go"
//   conditional  – amber solid  "Conditional"
//   monitor      – slate solid  "Monitor"
//
// go_no_go mapping (5 values → 4 visual tiers; no_go hidden):
//   primary      → "Primary"
//   go           → "Go"
//   conditional  → "Conditional"
//   monitor      → "Monitor"
//   no_go        → null (hidden)
// ---------------------------------------------------------------------------

export type ActionTier = 'primary' | 'go' | 'conditional' | 'monitor';

const ACTION_TIER_LABELS: Record<ActionTier, string> = {
  primary: 'Primary',
  go: 'Go',
  conditional: 'Conditional',
  monitor: 'Monitor',
};

const ACTION_TIER_COLORS: Record<ActionTier, string> = {
  primary: 'bg-slate-900 text-white',
  go: 'bg-emerald-600 text-white',
  conditional: 'bg-amber-500 text-white',
  monitor: 'bg-slate-500 text-white',
};

export function goNoGoToActionTier(value: GoNoGo): ActionTier | null {
  switch (value) {
    case 'primary':
      return 'primary';
    case 'go':
      return 'go';
    case 'conditional':
      return 'conditional';
    case 'monitor':
      return 'monitor';
    case 'no_go':
      return null;
  }
}

const ACTION_TIER_SET = new Set<ActionTier>(['primary', 'go', 'conditional', 'monitor']);

export function ActionChip({
  value,
  label,
  className,
}: {
  /** Either a GoNoGo enum or a resolved ActionTier. */
  value: GoNoGo | ActionTier;
  /** Optional label override (e.g. for tier counts like "1 Primary"). */
  label?: string;
  className?: string;
}) {
  const tier: ActionTier | null = ACTION_TIER_SET.has(value as ActionTier)
    ? (value as ActionTier)
    : goNoGoToActionTier(value as GoNoGo);
  if (!tier) return null;
  return (
    <Chip
      tone="solid"
      label={label ?? ACTION_TIER_LABELS[tier]}
      colorClass={ACTION_TIER_COLORS[tier]}
      className={className}
    />
  );
}

export const ACTION_TIER_ORDER: ActionTier[] = ['primary', 'go', 'conditional', 'monitor'];
export { ACTION_TIER_LABELS };
